import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { toast } from "sonner";
import { isApiError } from "@/lib/api/errors";

/** 4xx responses are the server's final answer — only retry transport faults. */
function shouldRetry(failureCount: number, error: unknown) {
  if (failureCount >= 2) return false;
  if (isApiError(error)) {
    if (error.code === "aborted") return false;
    if (error.status === 0) return true; // network
    if (error.status === 429) return false; // honour Retry-After instead
    return error.status >= 500;
  }
  return false;
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only shout about background refetches of data already on screen;
        // first-load failures are rendered inline by the route.
        if (query.state.data === undefined) return;
        if (isApiError(error) && (error.code === "aborted" || error.status === 401)) return;
        toast.error("Couldn't refresh that", {
          description: isApiError(error)
            ? [error.message, error.code, error.requestId].filter(Boolean).join(" · ")
            : "Please try again.",
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: { retry: false },
    },
  });
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [client] = React.useState(createQueryClient);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
